// frontend/src/utils/timezoneUtils.js

/**
 * Detect user's timezone using browser API
 * @returns {String} - IANA timezone identifier (e.g., 'America/New_York'), or "" when it
 *                     cannot be determined
 *
 * Returns EMPTY, not a city, when detection fails. It used to return 'America/Chicago',
 * which is a hardcoded default: a user outside Central time whose browser refused the
 * API would be silently given Central, and because that value looks perfectly valid
 * nothing downstream could tell it apart from a genuine detection. Callers that need a
 * zone must require the user to choose one (policy 2026-10-03: timezone is a required,
 * actively chosen field and must never be defaulted).
 */
export function detectUserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch (err) {
    return '';
  }
}

/**
 * Format date and time for display with timezone
 * @param {String} dateStr - Date string (YYYY-MM-DD)
 * @param {String} timeStr - Time string (HH:MM)
 * @param {String} timezone - IANA timezone identifier
 * @returns {String} - Formatted string (e.g., "Wed, Dec 11, 2024 at 2:30 PM CST")
 */
export function formatDateTimeWithTimezone(dateStr, timeStr, timezone) {
  try {
    const dateTime = new Date(`${dateStr}T${timeStr}:00`);
    
    // Get date parts
    const dateOptions = {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: timezone
    };
    const datePart = dateTime.toLocaleDateString('en-US', dateOptions);
    
    // Get time parts with timezone
    const timeOptions = {
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
      timeZone: timezone
    };
    const timePart = dateTime.toLocaleTimeString('en-US', timeOptions);
    
    return `${datePart} at ${timePart}`;
  } catch (err) {
    return `${dateStr} ${timeStr}`;
  }
}

/**
 * Get timezone abbreviation for a given date/time
 * @param {String} dateStr - Date string (YYYY-MM-DD)
 * @param {String} timezone - IANA timezone identifier
 * @returns {String} - Timezone abbreviation (e.g., 'CST', 'CDT')
 */
export function getTimezoneAbbreviation(dateStr, timezone) {
  try {
    const date = new Date(`${dateStr}T12:00:00`);
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZoneName: 'short',
      timeZone: timezone
    });
    const parts = formatter.formatToParts(date);
    const tzPart = parts.find(p => p.type === 'timeZoneName');
    return tzPart ? tzPart.value : timezone;
  } catch (err) {
    return timezone;
  }
}

/**
 * Format time in 12-hour format with AM/PM
 * @param {String} time24 - Time in HH:MM format
 * @returns {String} - Time in 12-hour format (e.g., '2:30 PM')
 */
export function formatTime12Hour(time24) {
  if (!time24) return '';
  
  const [hours, minutes] = time24.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  
  return `${displayHours}:${String(minutes).padStart(2, '0')} ${period}`;
}

/**
 * Format time with timezone abbreviation
 * @param {String} time24 - Time in HH:MM format
 * @param {String} dateStr - Date string for DST accuracy
 * @param {String} timezone - IANA timezone identifier
 * @returns {String} - Time with timezone (e.g., '2:30 PM CST')
 */
export function formatTimeWithTimezone(time24, dateStr, timezone) {
  const time12 = formatTime12Hour(time24);
  const tzAbbr = getTimezoneAbbreviation(dateStr, timezone);
  return `${time12} ${tzAbbr}`;
}

/**
 * Check if two timezones are the same (handles aliases)
 * @param {String} tz1 - First timezone
 * @param {String} tz2 - Second timezone
 * @returns {Boolean} - True if same timezone
 */
export function isSameTimezone(tz1, tz2) {
  if (!tz1 || !tz2) return false;
  if (tz1 === tz2) return true;
  
  // Check if they resolve to the same offset at a given time
  try {
    const date = new Date();
    const formatter1 = new Intl.DateTimeFormat('en-US', { 
      timeZone: tz1, 
      hour: 'numeric', 
      hour12: false 
    });
    const formatter2 = new Intl.DateTimeFormat('en-US', { 
      timeZone: tz2, 
      hour: 'numeric', 
      hour12: false 
    });
    return formatter1.format(date) === formatter2.format(date);
  } catch (err) {
    return false;
  }
}

/**
 * Get user-friendly timezone name
 * @param {String} timezone - IANA timezone identifier
 * @returns {String} - Friendly name (e.g., 'Eastern Time')
 */
export function getTimezoneName(timezone) {
  const timezoneNames = {
    'America/New_York': 'Eastern Time',
    'America/Chicago': 'Central Time',
    'America/Denver': 'Mountain Time',
    'America/Los_Angeles': 'Pacific Time',
    'America/Phoenix': 'Arizona Time',
    'America/Anchorage': 'Alaska Time',
    'Pacific/Honolulu': 'Hawaii Time'
  };
  
  return timezoneNames[timezone] || timezone.replace(/_/g, ' ').replace('America/', '');
}
